import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  ZoomIn,
  ZoomOut,
  Trash2,
  Check,
  Sparkles,
  Loader2,
  Filter,
  Search,
  Settings,
  Layers,
  X,
  Tag,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDocumentContext } from "../context/DocumentContext";
import { AIProcessingOverlay } from "../components/AIProcessingOverlay";
import { getApiBase } from "../config";
import { getToken } from "../auth";

// --- CONFIGURATION ---
const API_BASE_URL = getApiBase();

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- TYPES ---
type RedactionSource =
  | "AI_SUGGESTION"
  | "USER_MANUAL"
  | "GLOBAL_CACHE"
  | "DOCUMENT_TEMPLATE";
type RedactionStatus = "PROPOSED" | "CONFIRMED";

type AIState = "idle" | "starting" | "working" | "done";

interface RulePolicy {
  action:
    | "REDACT_FULL"
    | "TRUNCATE"
    | "TRUNCATE_YEAR"
    | "IGNORE"
    | "IGNORE_TOLL_FREE";
  keep_visible?: number;
  desc?: string;
}

interface RedactionBox {
  id: string;
  pageIndex: number;
  x: number; // %
  y: number; // %
  width: number; // %
  height: number; // %
  source: RedactionSource;
  status: RedactionStatus;
  label: string; // e.g. "SSN", "DOB"
  text_content?: string;
  anchor_label?: string; // nearby label text, for burn-time re-localization
  anchor_hint?: string; // "same_line" | "below" | "above" | "column"
  column_headers?: string[]; // anchor_hint === "column": header captions the mark spans
  tag?: string; // "Other": the keyword the user typed to say what this box is (e.g. "Password")
}

const DEFAULT_RULES = [
  "SSN",
  "Credit Card",
  "Bank Account",
  "DOB",
  "Email",
  "Phone",
  "Address",
  "License Plate",
];

const SmartRedactionEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- CONTEXT ---
  const { file, analysisData, setTempRedactionPayload, signingDates, setSigningDates, processMode } = useDocumentContext();

  // --- WORKFLOW STATE ---
  const mode = (location.state as any)?.mode || "redact-only";
  const isComboMode = mode === "combo";

  // --- LOCAL STATE ---
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.2);
  const [fingerprintHash, setFingerprintHash] = useState<string | null>(null);
  const [fingerprintProfile, setFingerprintProfile] = useState<any>(null);
  const [lastScanSource, setLastScanSource] = useState<string | null>(null);
  const [lastCacheHit, setLastCacheHit] = useState<boolean | null>(null);

  // Scanning State
  const [isScanning, setIsScanning] = useState(false);
  const [scanState, setScanState] = useState<AIState>("idle");
  const [scanSubtitle, setScanSubtitle] = useState("Preparing scan…");
  const [scanProgress, setScanProgress] = useState(0);

  const isScanOverlayVisible = scanState !== "idle";

  const [showRules, setShowRules] = useState(false);
  const [redactions, setRedactions] = useState<RedactionBox[]>([]);

  // Rule State (From Backend)
  const [activePolicy, setActivePolicy] = useState<Record<string, RulePolicy>>(
    {},
  );
  const [jurisdiction, setJurisdiction] = useState<string>("Unknown");

  // Resizing State
  const [isResizing, setIsResizing] = useState(false);
  const [resizeMode, setResizeMode] = useState<"BOTH" | "X" | "Y">("BOTH");
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });

  // Drawing & Tagging State
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState<Partial<RedactionBox> | null>(
    null,
  );
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);

  // Pending Box for Tagging
  const [pendingLabelBox, setPendingLabelBox] =
    useState<Partial<RedactionBox> | null>(null);

  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hasAutoScanned = useRef(false);
  const scanSeq = useRef(0);

  // Reset auto-scan flag when navigating to this page (location key changes on each navigate)
  const locationKey = location.key;
  useEffect(() => {
    hasAutoScanned.current = false;
  }, [locationKey]);

  // --- EFFECT: SAFETY CHECK ---
  useEffect(() => {
    if (!file) navigate("/");
  }, [file, navigate]);

  // --- HELPER: FETCH POLICY ---
  // This calls the new lightweight endpoint to update UI instantly
  const fetchPolicy = async (stateCode: string) => {
    try {
      console.log(`📡 Fetching rules for ${stateCode}...`);
      const res = await fetch(`${API_BASE_URL}/policy/${stateCode}`);
      if (res.ok) {
        const data = await res.json();
        setActivePolicy(data.rules || {});
        setJurisdiction(data.jurisdiction || stateCode);
        return data.rules; // Return for immediate use in scan
      }
    } catch (err) {
      console.error("Failed to fetch policy:", err);
    }
    return {};
  };

  // --- EFFECT: AUTO SCAN ---
  useEffect(() => {
    const init = async () => {
      if (file && !hasAutoScanned.current) {
        hasAutoScanned.current = true;

        // Look for state, but default to "FEDERAL" so the API is ALWAYS called
        const state =
          analysisData?.jurisdiction_and_context?.jurisdiction_state ||
          "FEDERAL";

        console.log("🚀 Triggering Policy Fetch for:", state);
        const rules = await fetchPolicy(state); // This is your GET call

        console.log("🚀 Policy Loaded, starting Scan...");
        // For promissory_note flow, force a fresh MIke scan because the document
        // template cache doesn't yet persist signing_dates — a cache hit would
        // return signing_dates: [] and break form-level splitting downstream.
        const mustForce = processMode === "promissory_note";
        scanDocument(rules, mustForce); // This is your POST call
      }
    };
    init();
  }, [file, analysisData]);

  // --- EFFECT: INITIALIZATION SEQUENCE ---
  useEffect(() => {
    const initWorkflow = async () => {
      if (file && !hasAutoScanned.current) {
        hasAutoScanned.current = true;

        // 1. Identify State from Context (or default to FEDERAL)
        const stateCode =
          analysisData?.jurisdiction_and_context?.jurisdiction_state ||
          "FEDERAL";
        setJurisdiction(stateCode); // Optimistic Update

        // 2. Fetch Rules First (Blocking)
        const loadedPolicy = await fetchPolicy(stateCode);

        // 3. Start Scan (Passing the loaded policy explicitly)
        // We pass loadedPolicy because 'activePolicy' state won't update in this closure yet.
        // Promissory note flow: force a fresh MIke scan so signing_dates are detected
        // (template cache doesn't yet persist signing_dates).
        const mustForce = processMode === "promissory_note";
        void scanDocument(loadedPolicy, mustForce);
      }
    };

    initWorkflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, analysisData]); // Run once when file is ready

  // --- EFFECT: Rotate subtitle while overlay is visible ---
  useEffect(() => {
    if (!isScanOverlayVisible) return;

    const steps = [
      "Uploading PDF…",
      "Detecting jurisdiction…",
      "Locating PII…",
      "Applying rules…",
      "Mapping boxes…",
    ];

    let i = 0;
    const t = window.setInterval(() => {
      i++;
      setScanSubtitle((current) =>
        current ? current : steps[i % steps.length],
      );
    }, 1400);

    return () => window.clearInterval(t);
  }, [isScanOverlayVisible]);

  // --- FUNCTION: SCAN PII ---
  const scanDocument = useCallback(
    async (policyOverride?: Record<string, RulePolicy>, forceReanalyze: boolean = false) => {
      if (!file) return;

      const mySeq = ++scanSeq.current;
      let showTimer: number | undefined;

      setIsScanning(true);
      setScanProgress(0.06);
      setScanSubtitle("Uploading PDF…");
      setScanState("starting");

      showTimer = window.setTimeout(() => setScanState("working"), 250);
      const token = getToken();
      try {
        const formData = new FormData();
        formData.append("file", file);

        // ✅ FIX: Use Policy Override (from "Fetch First") if available, otherwise use State
        const currentRules = policyOverride || activePolicy;

        // Safety check: ensure we have at least default rules if everything is empty
        const rulesKeys = Object.keys(currentRules);
        const rulesToSend =
          rulesKeys.length > 0
            ? rulesKeys
            : ["SSN", "ACCOUNT_NUMBER", "CREDIT_CARD", "EMAIL"];

        formData.append("rules", JSON.stringify(rulesToSend));

        if (analysisData?.jurisdiction_and_context) {
          formData.append(
            "context",
            JSON.stringify(analysisData.jurisdiction_and_context),
          );
        }

        // Pass pre-computed fingerprint to avoid redundant recomputation in scan-pii
        if (analysisData?.meta?.fingerprint_hash) {
          formData.append("fingerprint_hash", analysisData.meta.fingerprint_hash);
        }
        if (analysisData?.meta?.fingerprint_profile) {
          formData.append(
            "fingerprint_profile",
            JSON.stringify(analysisData.meta.fingerprint_profile),
          );
        }

        if (forceReanalyze) {
          formData.append("force_reanalyze", "true");
        }

        setScanProgress(0.18);
        setScanSubtitle("Looking for PII…");

        const response = await fetch(`${API_BASE_URL}/scan-pii`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (scanSeq.current !== mySeq) return;

        setScanProgress(0.55);
        setScanSubtitle("Scanning for PII…");

        if (!response.ok) {
          console.error("Scan API returned error status");
          setScanSubtitle("Scan failed");
          return;
        }

        const data = await response.json();
        // ✅ Capture fingerprint from backend scan response
        setLastScanSource(data.scan_source ?? null);
        setLastCacheHit(
          typeof data.cache_hit === "boolean" ? data.cache_hit : null,
        );

        setFingerprintHash(data.fingerprint_hash ?? null);
        setFingerprintProfile(data.fingerprint_profile ?? null);

        if (scanSeq.current !== mySeq) return;

        setScanProgress(0.75);
        setScanSubtitle("Applying rules…");

        if (data.active_policy_metadata) {
          setActivePolicy(data.active_policy_metadata);
        }
        if (data.jurisdiction) {
          setJurisdiction(data.jurisdiction);
        }
        // Capture signing dates for promissory_note splitting (non-redactable metadata).
        // Cache-hit scans (DOCUMENT_TEMPLATE_CACHE, SEGMENT_CACHE) return signing_dates: []
        // because the template cache doesn't persist them yet. Only overwrite our state
        // when this scan actually ran MIke — otherwise an unrelated re-scan could
        // wipe out signing dates we got from a force-reanalyze a moment earlier.
        if (Array.isArray(data.signing_dates)) {
          const isCacheHit =
            data.scan_source === "DOCUMENT_TEMPLATE_CACHE" ||
            data.scan_source === "SEGMENT_CACHE" ||
            data.scan_source === "GLOBAL_CACHE";
          if (!isCacheHit || data.signing_dates.length > 0) {
            setSigningDates(data.signing_dates);
          }
        }

        setScanProgress(0.9);
        setScanSubtitle("Mapping boxes…");

        const suggestions: RedactionBox[] = (data.detected_pii || []).map(
          (item: any, idx: number) => {
            const box = item.box || item.box_2d || [0, 0, 0, 0];

            return {
              id: item.id || `ai-${idx}-${Date.now()}`,
              pageIndex: (item.page_number || 1) - 1,
              y: box[0] / 10,
              x: box[1] / 10,
              height: (box[2] - box[0]) / 10,
              width: (box[3] - box[1]) / 10,
              source: (item.source || "AI_SUGGESTION") as RedactionSource,
              // A finding is an auto-applied CONFIRMED redaction only when it
              // comes from a genuine template/cache recall (the doc was
              // recognized). Everything the brain freshly DETECTS — AI
              // suggestions and regex hits — is a reviewable PROPOSED prompt,
              // regardless of whether the scan ran as AI_SCAN, AI_RESCAN
              // (force-reanalyze, e.g. promissory_note) or PARTIAL_SEGMENT_CACHE.
              // Keying off scan_source alone wrongly auto-confirmed forced scans.
              status:
                item.source === "USER_MANUAL" ||
                item.source === "DOCUMENT_TEMPLATE" ||
                item.source === "SEGMENT_TEMPLATE" ||
                data.scan_source === "DOCUMENT_TEMPLATE_CACHE" ||
                data.scan_source === "SEGMENT_CACHE" ||
                data.scan_source === "GLOBAL_CACHE"
                  ? "CONFIRMED"
                  : "PROPOSED",
              label: item.label || "Unknown",
              text_content: item.text_content,
              anchor_label: item.anchor_label,
              anchor_hint: item.anchor_hint,
              column_headers: item.column_headers,
              tag: item.tag,
            };
          },
        );

        setRedactions((prev) => {
          const manuals = prev.filter((r) => r.source === "USER_MANUAL");
          const newIds = new Set(suggestions.map((s) => s.id));
          const filteredManuals = manuals.filter((m) => !newIds.has(m.id));
          return [...filteredManuals, ...suggestions];
        });

        setScanProgress(1);
        setScanSubtitle("Done");
      } catch (error) {
        console.error("Failed to scan document:", error);
        setScanSubtitle("Scan failed");
        alert("Scan Failed. Is the backend server running?");
      } finally {
        if (scanSeq.current === mySeq) {
          setIsScanning(false);
          if (showTimer) window.clearTimeout(showTimer);

          setScanState("done");
          window.setTimeout(() => setScanState("idle"), 220);
          window.setTimeout(() => setScanProgress(0), 450);
        }
      }
    },
    [file, activePolicy, analysisData],
  );

  // --- ACTIONS ---
  const confirmRedaction = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setRedactions((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: "CONFIRMED" } : r)),
    );
    setSelectedBoxId(null);
  };

  const deleteRedaction = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setRedactions((prev) => prev.filter((r) => r.id !== id));
    setSelectedBoxId(null);
  };

  const [otherTag, setOtherTag] = useState<string>("");

  const finalizeManualBox = (label: string, tag?: string) => {
    if (!pendingLabelBox) return;
    const cleanTag = (tag || "").trim().slice(0, 40);

    const newBox: RedactionBox = {
      id: `manual-${Date.now()}`,
      pageIndex: pendingLabelBox.pageIndex!,
      x: pendingLabelBox.x!,
      y: pendingLabelBox.y!,
      width: pendingLabelBox.width!,
      height: pendingLabelBox.height!,
      source: "USER_MANUAL",
      status: "CONFIRMED",
      label,
      ...(cleanTag ? { tag: cleanTag } : {}),
    };

    setRedactions((prev) => [...prev, newBox]);
    setPendingLabelBox(null);
    setOtherTag("");
  };

  const handleSaveToDatabase = async () => {
    const confirmedBoxes = redactions.filter((r) => r.status === "CONFIRMED");

    if (confirmedBoxes.length === 0) {
      if (!window.confirm("No redactions confirmed. Save clean document?"))
        return;
    }

    const finalPayload = confirmedBoxes.map((r) => ({
      page_number: r.pageIndex + 1,
      box_2d: [
        Math.round(r.y * 10),
        Math.round(r.x * 10),
        Math.round((r.y + r.height) * 10),
        Math.round((r.x + r.width) * 10),
      ],
      label: r.label,
      source: r.source,
      // carry anchors through so the burn-time resolver can re-localize per doc
      ...(r.anchor_label ? { anchor_label: r.anchor_label } : {}),
      ...(r.anchor_hint ? { anchor_hint: r.anchor_hint } : {}),
      // a "column" anchor is useless without its headers: dropping them here
      // made the backend persist header-less column zones that the edge then
      // read as same-line labels (boxes over "Amount" / "Description:")
      ...(r.column_headers?.length ? { column_headers: r.column_headers } : {}),
      ...(r.tag ? { tag: r.tag } : {}),
    }));

    // Use scan state first, fall back to analysisData metadata
    const fpHash = fingerprintHash || analysisData?.meta?.fingerprint_hash;
    if (!fpHash || fpHash === "unknown_hash") {
      alert("Fingerprint missing. Please re-scan the document and try again.");
      return;
    }

    // Recover printed labels for manually-marked zones (which have no AI anchor)
    // so they feed the edge's label dictionary and redact on every similar form,
    // not just by position. Best-effort: on failure we keep the original zones
    // (the label lexicon still covers common captions).
    let zonesToSave = finalPayload;
    if (file && finalPayload.some((z) => !z.anchor_label)) {
      try {
        const token = getToken();
        const inferForm = new FormData();
        inferForm.append("file", file);
        inferForm.append("zones", JSON.stringify(finalPayload));
        const inferRes = await fetch(`${API_BASE_URL}/infer-anchors`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: inferForm,
        });
        if (inferRes.ok) {
          const data = await inferRes.json();
          if (Array.isArray(data?.zones)) zonesToSave = data.zones;
        }
      } catch (e) {
        console.warn("anchor inference failed; saving zones as-is", e);
      }
    }

    const templatePayload = {
      fingerprint_hash: fpHash,
      fingerprint_profile: fingerprintProfile || analysisData?.meta?.fingerprint_profile || null,
      jurisdiction_code: jurisdiction || "FEDERAL",
      name: `Layout ${new Date().toLocaleDateString()}`,
      zones: zonesToSave,
      segments: fingerprintProfile?.segments || analysisData?.meta?.fingerprint_profile?.segments || [],
    };

    try {
      // Try common storage keys (adjust if your app uses a different key)
      const token = getToken();
      if (!token) {
        alert("You are not logged in (missing token). Please log in again.");
        return;
      }

      const saveResponse = await fetch(`${API_BASE_URL}/redact/save-template`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(templatePayload),
      });

      if (saveResponse.ok) {
        alert(
          "Success! Layout saved. Future documents like this will auto-redact.",
        );
        if (isComboMode) {
          handleProceedToSplit();
        } else {
          navigate("/");
        }
      } else {
        let errMsg = "Unknown error";
        try {
          const err = await saveResponse.json();
          errMsg = err?.detail || errMsg;
        } catch {
          // ignore
        }
        alert(`Save failed: ${errMsg}`);
      }
    } catch (error) {
      console.error("Save failed:", error);
      alert("Network error. Check console.");
    }
  };

  const handleProceedToSplit = async () => {
    const confirmedBoxes = redactions.filter((r) => r.status === "CONFIRMED");
    setTempRedactionPayload(confirmedBoxes);

    // For promissory note flow, refine SSN segments by signing dates so each
    // form within a borrower's page range becomes its own splittable segment.
    if (processMode === "promissory_note" && analysisData?.document_analysis) {
      try {
        const res = await fetch(`${API_BASE_URL}/analyze/refine-pnote-segments`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ssn_segments: analysisData.document_analysis,
            signing_dates: signingDates || [],
          }),
        });
        if (res.ok) {
          const refined = await res.json();
          if (Array.isArray(refined?.document_analysis)) {
            // Replace SSN-only segments with form-level segments in context
            analysisData.document_analysis = refined.document_analysis;
          }
        } else {
          console.warn("Refine endpoint returned non-OK; falling back to SSN-only segments");
        }
      } catch (err) {
        console.warn("Refine call failed; falling back to SSN-only segments:", err);
      }
    }

    navigate("/splitter", { state: { mode: "combo" } });
  };

  // --- MOUSE HANDLERS ---

  const handleMouseDown = (e: React.MouseEvent, pageIndex: number) => {
    const target = e.target as HTMLElement;

    // 1. HANDLE RESIZE CLICK
    if (target.classList.contains("resize-handle")) {
      e.preventDefault();
      e.stopPropagation();

      const box = redactions.find((r) => r.id === selectedBoxId);
      if (!box) return;

      // Detect Direction based on class name
      if (target.classList.contains("resize-x")) setResizeMode("X");
      else if (target.classList.contains("resize-y")) setResizeMode("Y");
      else setResizeMode("BOTH");

      setIsResizing(true);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        w: box.width,
        h: box.height,
      });
      return;
    }

    // 2. HANDLE BOX SELECTION (Don't start drawing if clicking a box)
    if (target.closest(".redaction-box")) return;

    // 3. START DRAWING LOGIC
    e.preventDefault();
    const pageEl = pageRefs.current[pageIndex];
    if (!pageEl) return;

    setSelectedBoxId(null);
    setPendingLabelBox(null);

    const rect = pageEl.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;

    setIsDrawing(true);
    setStartPos({ x: xPct, y: yPct });
    setCurrentRect({ pageIndex, x: xPct, y: yPct, width: 0, height: 0 });
  };

  const handleMouseMove = (e: React.MouseEvent, pageIndex: number) => {
    const pageEl = pageRefs.current[pageIndex];
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();

    // A. RESIZING LOGIC (Respects Axis)
    if (isResizing && selectedBoxId) {
      e.preventDefault();
      const deltaXPixels = e.clientX - resizeStart.x;
      const deltaYPixels = e.clientY - resizeStart.y;

      const deltaXPercent = (deltaXPixels / rect.width) * 100;
      const deltaYPercent = (deltaYPixels / rect.height) * 100;

      setRedactions((prev) =>
        prev.map((r) => {
          if (r.id === selectedBoxId) {
            // Calculate potential new dimensions
            const newWidth = Math.max(1, resizeStart.w + deltaXPercent);
            const newHeight = Math.max(1, resizeStart.h + deltaYPercent);

            return {
              ...r,
              // Only update if mode allows
              width:
                resizeMode === "BOTH" || resizeMode === "X"
                  ? newWidth
                  : r.width,
              height:
                resizeMode === "BOTH" || resizeMode === "Y"
                  ? newHeight
                  : r.height,
            };
          }
          return r;
        }),
      );
      return;
    }

    // B. DRAWING LOGIC
    if (!isDrawing || !currentRect || currentRect.pageIndex !== pageIndex)
      return;
    e.preventDefault();

    const currentX = ((e.clientX - rect.left) / rect.width) * 100;
    const currentY = ((e.clientY - rect.top) / rect.height) * 100;

    const safeX = Math.max(0, Math.min(100, currentX));
    const safeY = Math.max(0, Math.min(100, currentY));

    const width = Math.abs(safeX - startPos.x);
    const height = Math.abs(safeY - startPos.y);
    const x = Math.min(safeX, startPos.x);
    const y = Math.min(safeY, startPos.y);

    setCurrentRect({ ...currentRect, x, y, width, height });
  };

  const handleMouseUp = () => {
    if (isResizing) {
      setIsResizing(false);
      return;
    }
    if (isDrawing && currentRect) {
      if ((currentRect.width || 0) > 1 && (currentRect.height || 0) > 1) {
        setPendingLabelBox({
          pageIndex: currentRect.pageIndex,
          x: currentRect.x,
          y: currentRect.y,
          width: currentRect.width,
          height: currentRect.height,
        });
      }
      setIsDrawing(false);
      setCurrentRect(null);
    }
  };

  if (!file) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-white">
        <Loader2 className="animate-spin mr-2" /> Loading Editor...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col h-screen overflow-hidden font-sans text-gray-900 dark:text-slate-100 transition-colors duration-200">
      {/* --- TOP TOOLBAR --- */}
      <div className="h-16 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between px-6 shrink-0 z-20 shadow-sm transition-colors">
        {/* LEFT */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-500 dark:text-slate-400 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div>
            <h1 className="font-bold text-sm text-gray-900 dark:text-white max-w-[200px] truncate">
              {file.name}
            </h1>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
              <span className="flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-blue-500" />
                {isScanOverlayVisible ? "AI Scanning" : "AI Ready"}
              </span>
              <span className="bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded border border-gray-200 dark:border-slate-600 text-xs text-gray-600 dark:text-slate-300">
                {jurisdiction} Rules
              </span>
            </div>
          </div>
        </div>

        {/* MIDDLE */}
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-slate-900/50 p-1 rounded-lg border border-gray-200 dark:border-slate-700">
          <button
            onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
            className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded text-gray-600 dark:text-slate-300"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs font-mono w-12 text-center text-gray-700 dark:text-gray-300">
            {(scale * 100).toFixed(0)}%
          </span>
          <button
            onClick={() => setScale((s) => Math.min(2.5, s + 0.1))}
            className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded text-gray-600 dark:text-slate-300"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>

        {/* RIGHT */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowRules(!showRules)}
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition
              ${
                showRules
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
              }
            `}
          >
            <Settings className="w-3 h-3" />
            Rules
          </button>

          <button
            onClick={() => void scanDocument(undefined, true)}
            disabled={isScanning}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600 rounded-lg text-xs font-medium transition disabled:opacity-60 disabled:cursor-not-allowed text-gray-700 dark:text-slate-200"
          >
            {isScanning ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Search className="w-3 h-3" />
            )}
            {isScanning ? "Scanning..." : "Re-Scan"}
          </button>

          <div className="flex items-center gap-2 bg-gray-50 dark:bg-slate-700/50 p-1 rounded-lg border border-gray-200 dark:border-slate-600">
            <button
              onClick={() => void handleSaveToDatabase()}
              disabled={isScanning}
              className="px-4 py-1.5 rounded text-xs font-medium transition flex items-center gap-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-slate-600 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Save className="w-3 h-3" /> Save &amp; Train
            </button>

            {isComboMode && (
              <button
                onClick={handleProceedToSplit}
                className="flex items-center gap-1 px-4 py-1.5 rounded font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white shadow transition"
              >
                Split <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- MAIN CONTENT --- */}
      <div className="flex-1 overflow-hidden flex relative">
        {/* --- RULES SIDEBAR --- */}
        {showRules && (
          <div className="w-72 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 z-30 flex flex-col animate-in slide-in-from-left duration-200 shadow-xl">
            <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
              <h3 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Filter className="w-3 h-3" /> Jurisdiction: {jurisdiction}
              </h3>
              <button onClick={() => setShowRules(false)}>
                <X className="w-4 h-4 text-gray-400 dark:text-slate-500 hover:text-gray-900 dark:hover:text-white" />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto">
              {Object.keys(activePolicy).length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-slate-500 italic">
                  No specific rules loaded. Defaulting to Federal standard.
                </p>
              ) : (
                Object.entries(activePolicy).map(([key, rule]) => (
                  <div
                    key={key}
                    className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg border border-gray-200 dark:border-slate-600"
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-sm text-gray-800 dark:text-slate-200">
                        {key}
                      </span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${
                          rule.action === "REDACT_FULL"
                            ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400"
                            : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400"
                        }`}
                      >
                        {rule.action.replace("_", " ")}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      {rule.action === "TRUNCATE"
                        ? `Show last ${rule.keep_visible || 4} characters`
                        : rule.action === "TRUNCATE_YEAR"
                          ? "Show Year Only"
                          : "Full redaction required"}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* --- CANVAS --- */}
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-slate-900 p-8 flex justify-center relative">
          {/* AI LOADING OVERLAY */}
          <AIProcessingOverlay
            isVisible={isScanOverlayVisible}
            title={scanState === "done" ? "Done" : "AI Redaction"}
            subtitle={scanSubtitle || "Locating PII & Applying Rules..."}
            progress={scanProgress}
          />

          <div className="relative">
            <Document
              file={file}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              className="shadow-2xl"
            >
              {Array.from(new Array(numPages), (_, index) => (
                <div
                  /* w-fit: the wrapper MUST be exactly this page's width. Box
                     percentages are measured against it (getBoundingClientRect)
                     and replayed against the PDF page. As a full-width block it
                     took the WIDEST page's width, so in a packet mixing portrait
                     statements and landscape sheets every narrower page's boxes
                     were saved squeezed left by pageW/maxW (2026-11-03, Knovaone
                     26-24816: an account-number box landed 27% left of the number). */
                  key={`page_${index}`}
                  className="relative mb-8 group cursor-crosshair w-fit"
                  ref={(el) => (pageRefs.current[index] = el)}
                  onMouseDown={(e) => handleMouseDown(e, index)}
                  onMouseMove={(e) => handleMouseMove(e, index)}
                  onMouseUp={handleMouseUp}
                >
                  <Page
                    pageNumber={index + 1}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="bg-white shadow-lg"
                  />

                  {/* RENDER BOXES */}
                  {redactions
                    .filter((r) => r.pageIndex === index)
                    .map((rect) => {
                      const isProposed = rect.status === "PROPOSED";
                      const isSelected = selectedBoxId === rect.id;
                      const isCache = rect.source === "GLOBAL_CACHE";

                      return (
                        <div
                          key={rect.id}
                          className={`redaction-box absolute z-10 transition-all cursor-pointer border-2
                            ${
                              isProposed
                                ? "bg-yellow-400/20 border-yellow-500 hover:bg-yellow-400/30"
                                : isCache
                                  ? "bg-purple-500/30 border-purple-500"
                                  : "bg-black border-transparent hover:border-blue-500"
                            }
                            ${
                              isSelected
                                ? "ring-2 ring-blue-500 ring-offset-2"
                                : ""
                            }
                          `}
                          style={{
                            left: `${rect.x}%`,
                            top: `${rect.y}%`,
                            width: `${rect.width}%`,
                            height: `${rect.height}%`,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBoxId(rect.id);
                          }}
                        >
                          {/* RESIZE HANDLES */}
                          {isSelected && (
                            <>
                              {/* 1. CORNER (BOTH X & Y) - Bottom Right */}
                              <div className="resize-handle resize-xy absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-blue-500 rounded-full cursor-se-resize z-50 border-2 border-white shadow hover:scale-125 transition-transform" />

                              {/* 2. RIGHT EDGE (ONLY WIDTH) - Middle Right */}
                              <div className="resize-handle resize-x absolute top-1/2 -right-1.5 -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full cursor-e-resize z-50 border-2 border-white shadow hover:scale-125 transition-transform" />

                              {/* 3. BOTTOM EDGE (ONLY HEIGHT) - Bottom Center */}
                              <div className="resize-handle resize-y absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-4 h-4 bg-blue-500 rounded-full cursor-s-resize z-50 border-2 border-white shadow hover:scale-125 transition-transform" />
                            </>
                          )}

                          {/* ACTION MENU POPUP */}
                          {isSelected && !isResizing && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 text-xs p-3 rounded-lg shadow-xl w-64 z-50 border border-gray-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-200">
                              <div className="flex items-start gap-2 mb-2">
                                {rect.source === "AI_SUGGESTION" ? (
                                  <Sparkles className="w-4 h-4 text-purple-600" />
                                ) : rect.source === "GLOBAL_CACHE" ? (
                                  <Layers className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <Tag className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                                )}
                                <div>
                                  <p className="font-bold uppercase text-[10px] tracking-wide text-gray-500 dark:text-slate-500">
                                    {rect.source.replace("_", " ")}
                                  </p>
                                  <p className="font-bold text-sm">
                                    {rect.label}
                                  </p>
                                  {rect.text_content && (
                                    <p className="text-gray-500 dark:text-slate-400 font-mono text-[10px] mt-0.5 truncate max-w-[180px]">
                                      {rect.text_content}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="mt-3 flex gap-2">
                                {/* CONFIRM BUTTON (Only visible if proposed) */}
                                {isProposed && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevents selecting the box when clicking button
                                      confirmRedaction(rect.id, e);
                                    }}
                                    className="flex-1 flex items-center justify-center gap-1 rounded bg-green-600 py-1.5 font-bold text-white transition-colors hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-500"
                                    title="Confirm this suggestion"
                                  >
                                    <Check className="h-3 w-3" /> Confirm
                                  </button>
                                )}

                                {/* REJECT / DELETE BUTTON */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevents selecting the box when clicking button
                                    deleteRedaction(rect.id, e);
                                  }}
                                  className={`
                                      flex items-center justify-center gap-1 rounded py-1.5 font-bold transition-colors
                                      ${
                                        isProposed
                                          ? "flex-1 bg-gray-100 text-red-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-red-400 dark:hover:bg-slate-600" // Reject Style
                                          : "w-full bg-red-600 text-white hover:bg-red-700 shadow-sm" // Delete Style
                                      }
                                    `}
                                  title={
                                    isProposed
                                      ? "Reject suggestion"
                                      : "Delete redaction"
                                  }
                                >
                                  <Trash2 className="h-3 w-3" />{" "}
                                  {isProposed ? "Reject" : "Delete"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                  {/* MANUAL TAGGING POPOVER */}
                  {pendingLabelBox && pendingLabelBox.pageIndex === index && (
                    <div
                      className="absolute z-50 bg-white dark:bg-slate-800 p-3 rounded-lg shadow-2xl border border-gray-200 dark:border-slate-700 w-56 animate-in fade-in zoom-in-95"
                      style={{
                        left: `${
                          (pendingLabelBox.x || 0) +
                          (pendingLabelBox.width || 0) +
                          2
                        }%`,
                        top: `${pendingLabelBox.y}%`,
                      }}
                    >
                      <h4 className="text-gray-900 dark:text-white font-bold text-xs mb-2 flex items-center gap-2">
                        <Tag className="w-3 h-3" /> Label this box
                      </h4>
                      <div className="grid grid-cols-1 gap-1 max-h-48 overflow-y-auto">
                        {(Object.keys(activePolicy).length
                          ? Object.keys(activePolicy)
                          : [
                              "SSN",
                              "DOB",
                              "Credit Card",
                              "Address",
                              "Name",
                              "Other",
                            ]
                        )
                          // PASSWORD is its own category (fully redacted) but no
                          // state policy lists it, so always offer it.
                          .concat(["PASSWORD"])
                          .filter((label, i, arr) => arr.indexOf(label) === i)
                          .filter((label) => label.toLowerCase() !== "other")
                          .map((label) => (
                          <button
                            key={label}
                            onClick={(e) => {
                              e.stopPropagation(); // <--- CRITICAL FIX
                              e.preventDefault();
                              finalizeManualBox(label);
                            }}
                            onMouseDown={(e) => e.stopPropagation()} // Double safety
                            className="text-left text-xs px-2 py-1.5 hover:bg-blue-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 hover:text-blue-700 dark:hover:text-white rounded transition-colors"
                          >
                            {label}
                          </button>
                        ))}
                        {/* "Other": say what the box is with a keyword (e.g. "Password",
                            "Security word"). Stored on the zone as `tag`; the label
                            stays OTHER so no PII truncation rule is applied to it. */}
                        <div
                          className="mt-1 pt-1 border-t border-gray-200 dark:border-slate-700"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="text-xs text-gray-500 dark:text-slate-400 px-2 pb-1">Other — tag with a keyword</div>
                          <div className="flex gap-1 px-1">
                            <input
                              autoFocus={false}
                              value={otherTag}
                              maxLength={40}
                              placeholder="e.g. Password"
                              onChange={(e) => setOtherTag(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && otherTag.trim()) {
                                  e.preventDefault();
                                  finalizeManualBox("Other", otherTag);
                                }
                              }}
                              className="flex-1 text-xs px-2 py-1 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-200"
                            />
                            <button
                              disabled={!otherTag.trim()}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                finalizeManualBox("Other", otherTag);
                              }}
                              className="text-xs px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40"
                            >
                              Tag
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setPendingLabelBox(null)}
                        className="mt-2 w-full text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 py-1 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {/* DRAWING PREVIEW */}
                  {isDrawing &&
                    currentRect &&
                    currentRect.pageIndex === index && (
                      <div
                        className="absolute bg-blue-500/20 border-2 border-blue-500 z-20 border-dashed"
                        style={{
                          left: `${currentRect.x}%`,
                          top: `${currentRect.y}%`,
                          width: `${currentRect.width}%`,
                          height: `${currentRect.height}%`,
                        }}
                      />
                    )}
                </div>
              ))}
            </Document>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartRedactionEditor;
